// src/components/RiskMetricsPanel.jsx
// 현재 보유종목·실시간 비중 기반 사전(ex-ante) 리스크 지표 — 최근 1년 일간수익률로 산출.
import React, { useState, useEffect, useMemo } from "react";
import { portfolioExposure } from "../lib/exposure";
import { computeRiskMetrics } from "../lib/riskMetrics";

// KOSPI 벤치마크: 지수 심볼(^KS11)은 야후 차트 경로로 안 잡혀 KODEX200 ETF(069500,.KS)로 대체
const BENCH = { ticker: "069500", market: "KR", label: "KOSPI200" };

const pct = (n, d = 1) => (n == null || isNaN(n)) ? "—" : `${n >= 0 ? "" : ""}${(n * 100).toFixed(d)}%`;
const num = (n, d = 2) => (n == null || isNaN(n)) ? "—" : n.toFixed(d);

export default function RiskMetricsPanel({ holdings, marketData, exchangeRates }) {
  const [hist, setHist] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | done | error

  // 현재 부호비중(그로스 기준, Σ|w|=1)
  const weights = useMemo(() => {
    if (!holdings?.length) return [];
    const { rows, grossKRW } = portfolioExposure(holdings, marketData, exchangeRates);
    if (!grossKRW) return [];
    return rows.filter((r) => r.hasPrice)
      .map((r) => ({ ticker: r.ticker, market: r.market || "US", w: r.signedKRW / grossKRW }));
  }, [holdings, marketData, exchangeRates]);

  const symbolsKey = useMemo(
    () => weights.map((w) => `${w.ticker}:${w.market}`).sort().join(","),
    [weights]
  );

  useEffect(() => {
    if (!symbolsKey) { setHist(null); setStatus("idle"); return; }
    let alive = true;
    setStatus("loading");
    const syms = [...symbolsKey.split(","), `${BENCH.ticker}:${BENCH.market}`].join(",");
    fetch(`/api/history?symbols=${encodeURIComponent(syms)}&range=1y&interval=1d`)
      .then((r) => r.json())
      .then((j) => { if (alive) { setHist(j); setStatus("done"); } })
      .catch(() => { if (alive) setStatus("error"); });
    return () => { alive = false; };
  }, [symbolsKey]);

  const m = useMemo(() => {
    if (!hist?.series) return null;
    return computeRiskMetrics({ weights, series: hist.series, benchSeries: hist.series[BENCH.ticker] });
  }, [hist, weights]);

  const cards = m && !m.insufficient ? [
    { label: "연환산 변동성", value: pct(m.vol), sub: "낮을수록 안정", color: "#1a1a2e" },
    { label: "Sharpe", value: num(m.sharpe), sub: "무위험 3% 기준", color: (m.sharpe ?? 0) >= 1 ? "#0a7c59" : "#1a1a2e" },
    { label: "IR (KOSPI 대비)", value: num(m.irBench), sub: `TE ${pct(m.te)}`, color: (m.irBench ?? 0) >= 0.5 ? "#0a7c59" : "#1a1a2e" },
    { label: "IR (현금 대비)", value: num(m.irCash), sub: "마켓뉴트럴 가정", color: (m.irCash ?? 0) >= 0.5 ? "#0a7c59" : "#1a1a2e" },
    { label: "베타 (KOSPI)", value: num(m.beta), sub: "0에 가까울수록 중립", color: Math.abs(m.beta ?? 0) <= 0.2 ? "#0a7c59" : "#c0392b" },
    { label: "상관 (KOSPI)", value: num(m.corr), sub: "시장 동조성", color: "#1a1a2e" },
    { label: "사전 기대수익(연)", value: pct(m.annRet), sub: "과거1년 평균 환산", color: (m.annRet ?? 0) >= 0 ? "#0a7c59" : "#c0392b" },
  ] : [];

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#1a1a2e" }}>현재 포트폴리오 리스크 (사전 추정)</h3>
        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
          최근 1년 일간수익률 · 현 보유비중 고정(롱+/숏−, 그로스 기준) · 로컬통화(환변동 제외) · 거래일 교집합
          {m && !m.insufficient && <> · {m.covered}종목 · {m.n}일{m.benchDays ? ` (벤치 ${m.benchDays}일)` : ""}</>}
        </div>
      </div>

      {status === "loading" && <Note>📊 과거 시계열 불러오는 중…</Note>}
      {status === "error" && <Note>⚠️ 시계열 조회 실패 — 잠시 후 새로고침</Note>}
      {status === "idle" && <Note>보유 종목이 없거나 실시간 시세가 아직 없습니다.</Note>}
      {status === "done" && m?.insufficient && <Note>데이터 부족 — 공통 거래일 {m.n}일 (최소 20일 필요). 신규상장·거래정지 종목 확인.</Note>}

      {cards.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
          {cards.map((c) => (
            <div key={c.label} style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, padding: "14px 16px", borderTop: `3px solid ${c.color}` }}>
              <div style={{ fontSize: 10.5, color: "#888", marginBottom: 6, letterSpacing: "0.03em", textTransform: "uppercase" }}>{c.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: c.color, lineHeight: 1.2 }}>{c.value}</div>
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{c.sub}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Note({ children }) {
  return <div style={{ background: "#fafafa", border: "1px solid #eee", borderRadius: 10, padding: "16px 18px", fontSize: 13, color: "#888" }}>{children}</div>;
}
